import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Calculator, TrendingDown, Crown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ToolPricing {
  name: string;
  logo: string;
  url: string;
  calculate: (users: number) => number;
  note: string;
  isRecommended?: boolean;
}

const toolPricing: ToolPricing[] = [
  {
    name: "Lottae",
    logo: "🏆",
    url: "https://www.lottae.com/pricing",
    calculate: (users: number) => 7 + (users * 3.5),
    note: "All features included",
    isRecommended: true,
  },
  {
    name: "Asana",
    logo: "📋",
    url: "https://asana.com",
    calculate: (users: number) => users <= 10 ? 0 : users * 10.99,
    note: "Premium tier",
  },
  {
    name: "Monday.com",
    logo: "📊",
    url: "https://monday.com",
    calculate: (users: number) => Math.max(3, users) * 9,
    note: "Basic tier (min 3 seats)",
  },
  {
    name: "Trello",
    logo: "📌",
    url: "https://trello.com",
    calculate: (users: number) => users * 5,
    note: "Standard tier",
  },
  {
    name: "Jira",
    logo: "🔧",
    url: "https://www.atlassian.com/software/jira",
    calculate: (users: number) => users <= 10 ? 0 : users * 7.75,
    note: "Standard tier",
  },
  {
    name: "ClickUp",
    logo: "✅",
    url: "https://clickup.com",
    calculate: (users: number) => users * 7,
    note: "Unlimited tier",
  },
  {
    name: "Notion",
    logo: "📝",
    url: "https://notion.so",
    calculate: (users: number) => users * 10,
    note: "Plus tier",
  },
];

export const PMToolsCalculator = () => {
  const [teamSize, setTeamSize] = useState(10);

  const calculations = useMemo(() => {
    return toolPricing.map(tool => ({
      ...tool,
      monthlyCost: tool.calculate(teamSize),
      yearlyCost: tool.calculate(teamSize) * 12,
    })).sort((a, b) => a.monthlyCost - b.monthlyCost);
  }, [teamSize]);

  const lottaeCost = calculations.find(t => t.name === "Lottae")?.monthlyCost || 0;
  const highestCost = Math.max(...calculations.map(t => t.monthlyCost));
  const avgCompetitorCost = calculations
    .filter(t => t.name !== "Lottae" && t.monthlyCost > 0)
    .reduce((sum, t, _, arr) => sum + t.monthlyCost / arr.length, 0);

  const yearlySavings = (avgCompetitorCost - lottaeCost) * 12;

  return (
    <section className="py-8">
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <Calculator className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl sm:text-2xl">Cost Calculator</CardTitle>
              <p className="text-muted-foreground text-sm mt-1">
                Compare monthly costs based on your team size
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-8">
          {/* Team Size Selector */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Team Size
              </label>
              <Badge variant="secondary" className="text-lg px-4 py-1">
                {teamSize} users
              </Badge>
            </div>
            <Slider
              value={[teamSize]}
              onValueChange={(value) => setTeamSize(value[0])}
              min={5}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>5 users</span>
              <span>50 users</span>
              <span>100 users</span>
            </div>
          </div>

          {/* Savings Highlight */}
          {yearlySavings > 0 && (
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2 rounded-full bg-primary/20">
                  <TrendingDown className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Save with Lottae vs. competitor average
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    ${yearlySavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cost Comparison Bars */}
          <div className="space-y-3">
            {calculations.map((tool) => {
              const widthPercent = highestCost > 0 
                ? (tool.monthlyCost / highestCost) * 100 
                : 0;
              
              return (
                <div 
                  key={tool.name}
                  className={`p-4 rounded-lg border transition-all ${
                    tool.isRecommended 
                      ? "border-primary bg-primary/5" 
                      : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{tool.logo}</span>
                      {tool.isRecommended && (
                        <Crown className="w-4 h-4 text-yellow-500" />
                      )}
                      <span className={`font-medium ${tool.isRecommended ? "text-primary" : ""}`}>
                        {tool.name}
                      </span>
                      {tool.isRecommended && (
                        <Badge className="bg-primary text-primary-foreground text-xs">
                          Best Value
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-bold ${tool.isRecommended ? "text-primary" : ""}`}>
                        ${tool.monthlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                  </div>
                  
                  {/* Cost Bar */}
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        tool.isRecommended 
                          ? "bg-primary" 
                          : "bg-muted-foreground/30"
                      }`}
                      style={{ width: `${Math.max(widthPercent, 2)}%` }}
                    />
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {tool.note} • ${(tool.yearlyCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="pt-4 text-center">
            <Button asChild size="lg" className="gap-2">
              <a 
                href="https://www.lottae.com/pricing" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Crown className="w-4 h-4" />
                Get Started with Lottae
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              $7/mo base + $3.50/user • No feature restrictions
            </p>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
